import type { ConsentCategory, ConsentState } from './polarisConsent.types';

/**
 * The Segment destinations that need the visitor's consent, named exactly as Segment names them.
 *
 * Segment loads every device-mode destination `integrations` leaves out, and analytics.js copies
 * the load-time flags into every event, where Segment uses them to route events to cloud-mode
 * destinations. So each destination that needs consent is named here, whether it runs in the
 * browser or on Segment's servers. `All: false` is not an option: it would also switch off every
 * unlisted cloud-mode destination (S3, Iterable, Salesforce) for every visitor.
 *
 * `Amplitude` is the classic destination's name, which joy-web's page calls still set per event.
 */
export const CONSENT_GATED_DESTINATIONS: Readonly<Record<string, ConsentCategory>> = {
  'Actions Amplitude': 'statistics',
  Amplitude: 'statistics',
  'Google Analytics 4 Web': 'statistics',
  'Visual Tagger': 'statistics',
  'Facebook Pixel': 'marketing',
  'Bing Ads': 'marketing',
  Criteo: 'marketing',
  'Pinterest Tag': 'marketing',
  'Facebook Conversions API (Actions)': 'marketing',
  'OpenAI Ads Connector (Joy Life)': 'marketing',
};

/**
 * A `false` flag for each destination whose category `consent` denies. Granted destinations are
 * left out rather than set to `true`, so per-event flags such as `Amplitude: false` keep working.
 */
export const declinedIntegrations = (consent: ConsentState): Record<string, false> =>
  Object.fromEntries(
    Object.entries(CONSENT_GATED_DESTINATIONS)
      .filter(([, category]) => consent[category] !== true)
      .map(([destination]): [string, false] => [destination, false]),
  );

export type SourceMiddleware = (args: Readonly<{ payload: { obj: { integrations?: Record<string, unknown> } }; next: (payload: unknown) => void }>) => void;

/**
 * A Segment source middleware that switches off, on every event, each destination `getConsent`
 * denies at that moment, whatever the event's own `integrations` say. analytics.js merges per-event
 * flags over the load-time ones, so without it a call site could turn a declined destination back
 * on. Reading consent per event also stops cloud-mode destinations as soon as a visitor opts out.
 */
export const enforceConsentMiddleware =
  (getConsent: () => ConsentState): SourceMiddleware =>
  ({ payload, next }) => {
    payload.obj.integrations = { ...payload.obj.integrations, ...declinedIntegrations(getConsent()) };
    next(payload);
  };
